/**
 * ArtifactDrawer — side drawer (role=dialog) for artifact detail (GH-256).
 *
 * Accessibility:
 *   - role="dialog" + aria-modal="true" + aria-label
 *   - Focus trap: first focusable element receives focus on open; Tab cycles within
 *   - ESC closes the drawer
 *   - Backdrop click closes the drawer
 *
 * Slots reserved for future phases:
 *   - ArtifactGraphLink (Phase 3 / GH-258) — rendered but no-op
 *   - hostedSiteUrl in ArtifactExportReadiness (Phase 4 / GH-259) — optional field
 */

import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useArtifactDetail, storageFileToArtifactDetail } from './use-artifact-detail'
import { ArtifactProvenanceSection } from './artifact-provenance-section'
import { ArtifactQualitySection } from './artifact-quality-section'
import { ArtifactPackageMembership } from './artifact-package-membership'
import { ArtifactReviewState } from './artifact-review-state'
import { ArtifactExportReadiness } from './artifact-export-readiness'
import { ArtifactGraphLink } from './artifact-graph-link'
import type { StorageFile } from '../../hooks/use-storage'

interface Props {
  /** Upload ID to show; null means drawer is closed */
  artifactId: string | null
  /** Prefilled row from the storage list — avoids an extra round-trip */
  sourceFile?: StorageFile | null
  onClose: () => void
  isDark: boolean
}

// ── Focus trap ─────────────────────────────────────────────────────────────────

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function useFocusTrap(ref: React.RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active || !ref.current) return
    const el = ref.current
    const focusables = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (focusables.length) focusables[0].focus()

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const els = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (!els.length) return
      const first = els[0]
      const last = els[els.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    el.addEventListener('keydown', handleKey)
    return () => el.removeEventListener('keydown', handleKey)
  }, [active, ref])
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ArtifactDrawer({ artifactId, sourceFile, onClose, isDark }: Props) {
  const { t } = useTranslation('artifact')
  const drawerRef = useRef<HTMLDivElement>(null)
  const open = Boolean(artifactId)

  // Build initialData from sourceFile if present
  const initialData = sourceFile ? storageFileToArtifactDetail(sourceFile) : undefined
  const { data: artifact, isLoading, isError } = useArtifactDetail(artifactId, initialData)

  // ESC to close
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Focus trap
  useFocusTrap(drawerRef, open)

  // Restore focus to trigger element on close
  const triggerRef = useRef<Element | null>(null)
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement
    } else {
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus()
    }
  }, [open])

  const handleBackdropClick = useCallback(() => {
    onClose()
  }, [onClose])

  if (!open) return null

  return (
    <>
      {/* Backdrop — aria-hidden so screen readers stay on the dialog */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={handleBackdropClick}
        aria-hidden="true"
        data-testid="artifact-drawer-backdrop"
      />

      {/* Drawer panel — sibling of backdrop so ARIA role=dialog is accessible */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('drawer.title')}
        className={cn(
          'fixed top-0 right-0 z-50 flex h-full w-full flex-col shadow-2xl sm:w-[480px]',
          'animate-slide-in-right',
          isDark
            ? 'bg-surface-1 border-l border-white/[0.06]'
            : 'bg-white border-l border-neutral-200',
        )}
        onClick={(e) => e.stopPropagation()}
        data-testid="artifact-drawer"
      >
        {/* Header */}
        <div className={cn('flex items-center justify-between border-b px-4 py-3 shrink-0', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
          <span className={cn('text-sm font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
            {t('drawer.title')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('drawer.close')}
            className={cn(
              'rounded-md p-1.5 transition-colors',
              isDark ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-200' : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700',
            )}
            data-testid="artifact-drawer-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {isLoading && !artifact && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('loading')}
            </div>
          )}

          {isError && !artifact && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {t('error')}
            </div>
          )}

          {artifact && (
            <>
              {/* 1. Provenance */}
              <ArtifactProvenanceSection
                uploadId={artifact.id}
                filename={artifact.filename}
                contentType={artifact.contentType}
                sizeBytes={artifact.sizeBytes}
                uploadedAt={artifact.uploadedAt}
                extractionStatus={artifact.extractionStatus}
                markdownState={artifact.markdownState}
                isDark={isDark}
              />

              <Divider isDark={isDark} />

              {/* 2. Quality */}
              <ArtifactQualitySection
                extractionState={artifact.extractionStatus}
                confidence={null}
                needsOcr={null}
                isDark={isDark}
              />

              <Divider isDark={isDark} />

              {/* 3. Package membership */}
              <ArtifactPackageMembership
                packageId={artifact.packageId}
                packageName={artifact.packageName}
                isDark={isDark}
              />

              <Divider isDark={isDark} />

              {/* 4. Review state — Phase 2 (GH-257) will enrich with real queue data */}
              <ArtifactReviewState
                inQueue={false}
                reviewerAssigned={null}
                isDark={isDark}
              />

              <Divider isDark={isDark} />

              {/* 5. Export readiness */}
              <ArtifactExportReadiness
                bundleReady={artifact.markdownState === 'ready'}
                hostedSiteUrl={artifact.hostedSiteUrl}
                isDark={isDark}
              />

              <Divider isDark={isDark} />

              {/* 6. Graph link — Phase 3 slot (GH-258) */}
              <ArtifactGraphLink artifactId={artifact.id} isDark={isDark} />
            </>
          )}
        </div>
      </div>
    </>
  )
}

function Divider({ isDark }: { isDark: boolean }) {
  return <hr className={cn('border-0 border-t', isDark ? 'border-white/[0.06]' : 'border-neutral-200')} />
}
